import "./list-shift.scss";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Sidebar } from "primereact/sidebar";
import { CiSearch } from "react-icons/ci";
import { MdClear } from "react-icons/md";

import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import ErrorSection from "@shared/ui/error-section/error-section";
import SearchInput from "@shared/ui/search-input/search-input";
import HeaderContent from "@shared/ui/header-content/header-content";
import { useQyGetShift } from "@core/query/shift.query";
import { GetShiftModel } from "@core/model/query-model-shift";
import { TableSort } from "@core/model/table-sort";
import { FilterShift } from "../filter-shift/filter-shift";
import { useFilterShiftContext } from "../filter-shift/filter-shift.context";
import { useFormDropdownShiftTypeContext } from "@domain/shift-type/form-dropdown-shift-type/form-dropdown-shift-type.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { useUserIdentityContext } from "@core/services/user-identity.context";

export function Shift() {
  // THIS IS THE LOCAL DECLARATION
  const { mappedShiftType } = useFormDropdownShiftTypeContext();
  const { userIsManagement } = useUserIdentityContext();
  const { formatTime, getTime } = useDateTimeContext();
  const navigate = useNavigate();
  const [rowLimit, setRowLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [tableSort, setTableSort] = useState<TableSort>({
    sortField: undefined,
    sortOrder: undefined,
  });
  const [sort, setSort] = useState({});
  const [filterPanel, setFilterPanel] = useState(false);
  const { filterChipsElement, filterEntity } = useFilterShiftContext();

  // THIS IS THE QUERY DECLARATION
  const {
    data: shiftResponse,
    isLoading,
    isError,
    error,
  } = useQyGetShift(searchTerm, rowLimit, pageNumber, sort, filterEntity);

  // THIS IS THE LOCAL DECLARATION
  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };
  const handleTableSort = (e: DataTableStateEvent) => {
    setTableSort({
      sortField: e.sortField,
      sortOrder: e.sortOrder,
    });
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    setSort({ [e.sortField]: order });
  };
  const navigateToRecord = (item: GetShiftModel) => {
    navigate(`${item.code}`);
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };
  const handleNew = () => {
    navigate(`new`);
  };

  // THIS IS THE LOCAL RENDERS
  const displayColType = (type: string) => {
    const selected = mappedShiftType.filter((x) => x.value === type);
    return <b>{selected[0]?.label || "-"}</b>;
  };
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const filterElement = (
    <section>
      <div className="flex gap-4 items-center mb-2">
        <SearchInput
          clearIcon={<MdClear />}
          searchIcon={<CiSearch />}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          placeholder="Search items"
          className="w-full block"
        />
        {/* <Button
          label="Filter"
          severity="secondary"
          badgeClassName="p-badge-danger"
          onClick={() => setFilterPanel(true)}
          outlined
        /> */}
      </div>

      {filterChipsElement}
      <Sidebar visible={filterPanel} onHide={() => setFilterPanel(false)}>
        <FilterShift />
      </Sidebar>
    </section>
  );
  const grid = (
    <section className="mt-6">
      <h5 className="mb-2">
        Total: <b>{shiftResponse?.count}</b>
      </h5>

      <DataTable
        value={shiftResponse?.data}
        selectionMode="single"
        onSelectionChange={(e) => navigateToRecord(e.value)}
        onSort={handleTableSort}
        sortField={tableSort.sortField}
        sortOrder={tableSort.sortOrder}
      >
        <Column header="Title" field="title"></Column>
        <Column
          header="Type"
          body={(cell) => displayColType(cell.type)}
        ></Column>
        <Column
          body={(cell) => formatTime(getTime(cell.start))}
          header="Start"
          sortable
        ></Column>
        <Column
          body={(cell) => formatTime(getTime(cell.end))}
          header="End"
          sortable
        ></Column>
        <Column field="break" header="Break (Minutes)"></Column>
        <Column field="half_day" header="HalfDay (Minutes)"></Column>
        <Column field="grace_period" header="GracePeriod (Minutes)"></Column>
        <Column field="work_hours" header="WorkHours (Minutes)"></Column>
      </DataTable>

      <Paginator
        first={first}
        rows={rowLimit}
        totalRecords={shiftResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </section>
  );

  return (
    <div id="Shift" className="shift">
      <HeaderContent title="Shift">
        <span className="flex gap-2">
          {userIsManagement() ? <Button label="New" onClick={handleNew} /> : null}
        </span>
      </HeaderContent>

      <div className="p-7">
        {filterElement}
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && grid}
      </div>
    </div>
  );
}

export default Shift;
